import rawSnapshot from 'virtual:cpq-local-catalog';
import type { Equipment } from '../../../api/active/generated/equipment';
import { hydrateDeviceCatalogRecord } from '../state/deviceCatalog';
import type { CpqLoadResult } from '../state/repository';
import type { CpqState, PlanCatalogRecord, StorageLike } from '../state/types';
import {
  loadLocalCatalogSnapshot,
  projectLocalCatalogDtos,
} from './localCatalogSnapshot';
import {
  type LocalCatalogState,
  readLocalCatalogState,
} from './localCatalogState';
import { seedLocalDemoCatalogImages } from './localDemoCatalogImages';
import { seedLocalDemoScenarios } from './localDemoScenarios';
import { getLocalModuleCatalogProjection } from './localModuleTransport';

let cached: ReturnType<typeof loadLocalCatalogSnapshot> | undefined;

const LOCAL_DEMO_PACKAGE_ITEM_PRICE_CENTS = 10_000;

function isExplicitLocalDemoEquipment(item: Equipment): boolean {
  return (
    item.skuCode?.startsWith('LOCAL-DEMO-') === true ||
    item.name?.startsWith('本地演示｜') === true
  );
}

/** Seed only absent prices on named demo devices; null and existing values are user data. */
export function seedLocalDemoPackageItemPrices(
  equipment: readonly Equipment[],
): Equipment[] {
  return equipment.map((item) =>
    isExplicitLocalDemoEquipment(item) && item.packageItemPrice === undefined
      ? { ...item, packageItemPrice: LOCAL_DEMO_PACKAGE_ITEM_PRICE_CENTS }
      : item,
  );
}

function seedLocalDemoCatalogPackageItemPrices(
  state: LocalCatalogState,
): LocalCatalogState {
  return {
    ...state,
    equipmentDtos: seedLocalDemoPackageItemPrices(state.equipmentDtos),
    equipmentRevisionDtos: seedLocalDemoPackageItemPrices(
      state.equipmentRevisionDtos,
    ),
  };
}

function projectLocalDemoPlanDiscount(
  plan: PlanCatalogRecord,
): PlanCatalogRecord {
  if (
    plan.formalDiscount !== null ||
    !plan.currentRevisionId ||
    !plan.formalDerivedDiscount?.trim()
  )
    return plan;
  const derivedDiscount = Number(plan.formalDerivedDiscount);
  if (
    !Number.isFinite(derivedDiscount) ||
    derivedDiscount < 0 ||
    derivedDiscount > 100
  )
    return plan;
  // Local quotes use this already frozen total-derived ratio as the paired
  // plan snapshot. Exact per-line cents remain authoritative for pricing.
  return { ...plan, formalDiscount: derivedDiscount };
}

export function localDemoStorage(base: StorageLike): StorageLike {
  const keyFor = (key: string) => `cpq-local-database-demo:v1:${key}`;
  return {
    getItem: (key) => base.getItem(keyFor(key)),
    setItem: (key, value) => base.setItem(keyFor(key), value),
    removeItem: (key) => base.removeItem(keyFor(key)),
  };
}

/** Synchronize editable demo directories without changing quote snapshots. */
export function refreshLocalDemoCatalog(state: CpqState): CpqState {
  const snapshot = getLocalDemoCatalog();
  if (!snapshot) return state;
  const modules = getLocalModuleCatalogProjection(snapshot);
  // The disposable module transport names its sample owner independently. Map
  // that owner onto the protected local super-admin identity instead of adding
  // a second super-admin that makes the v5 state fail its safety invariant.
  const demoUser =
    state.catalog.users.find((user) => user.id === 'U-000') ??
    state.catalog.users.find((user) => user.id === 'offline-demo-user');
  const users = [
    ...state.catalog.users.filter(
      (user) => user.id !== 'U-000' && user.id !== 'offline-demo-user',
    ),
    ...(demoUser
      ? [{ ...demoUser, id: 'U-000', displayName: '本地演示用户' }]
      : []),
  ];
  const customerManagerId = (id: string | undefined) =>
    id === 'offline-demo-user' ? 'U-000' : id;
  return {
    ...state,
    catalog: {
      ...state.catalog,
      ...snapshot.catalog,
      users,
      customers: modules.customers.map((customer) => ({
        ...customer,
        managerUserId: customerManagerId(customer.managerUserId),
      })),
      warehouses: modules.warehouses,
      devices: snapshot.catalog.devices.map((device) =>
        hydrateDeviceCatalogRecord({
          ...device,
          // The disposable quote model has a narrower vocabulary than the API.
          // Keep the source text intact in the local DTO store and management page.
          procurementAttribute:
            device.procurementAttribute === '可采购'
              ? '可以采购'
              : ['可以采购', '不可采购', '暂停采购'].includes(
                    device.procurementAttribute ?? '',
                  )
                ? device.procurementAttribute
                : undefined,
          stockByWarehouse: Object.fromEntries(
            Object.entries(modules.inventoryByDeviceId[device.id] ?? {}).filter(
              (entry): entry is [string, number] =>
                typeof entry[1] === 'number',
            ),
          ),
        }),
      ),
    },
  };
}

/** Only the explicit loopback development demo may consume the private snapshot. */
export function getLocalDemoCatalog() {
  if (
    !import.meta.env.DEV ||
    (import.meta.env as ImportMetaEnv & { VITE_CPQ_OFFLINE_DEMO?: string })
      .VITE_CPQ_OFFLINE_DEMO !== 'true' ||
    typeof window === 'undefined' ||
    !['localhost', '127.0.0.1'].includes(window.location.hostname) ||
    !rawSnapshot
  )
    return null;
  cached ??= loadLocalCatalogSnapshot(rawSnapshot);
  const baseline: LocalCatalogState = {
    equipmentDtos: cached.equipmentDtos,
    packageDtos: cached.packageDtos,
    planDtos: cached.planDtos,
    equipmentRevisionDtos: cached.equipmentDtos,
    packageRevisionDtos: cached.packageRevisionDtos,
    planRevisionDtos: cached.planDtos,
  };
  const stored = seedLocalDemoCatalogImages(
    seedLocalDemoCatalogPackageItemPrices(readLocalCatalogState(cached)),
    baseline,
  );
  const catalog = projectLocalCatalogDtos(stored);
  return {
    ...stored,
    catalog: {
      ...catalog,
      plans: catalog.plans.map(projectLocalDemoPlanDiscount),
    },
  };
}

export function loadLocalDemoState(loaded: CpqLoadResult): CpqLoadResult {
  const snapshot = getLocalDemoCatalog();
  if (!snapshot) return loaded;
  const state = structuredClone(loaded.state);
  if (loaded.status === 'missing') {
    // This namespace starts with a blank rehearsal, never the fixture order history.
    const blank = state.quotes[state.activeQuoteId];
    state.quotes = { [blank.quoteId]: blank };
    state.openQuoteIds = [blank.quoteId];
    state.shares = [];
    state.negotiationLinks = [];
    state.publications = [];
    state.negotiationDrafts = [];
    state.receipts = [];
    state.exportTasks = [];
    state.stockReminders = [];
    state.activities = [];
    state.audits = [];
    state.temporaryDevices = [];
    state.stockOverrides = {};
  }
  const refreshed = refreshLocalDemoCatalog(state);
  return {
    ...loaded,
    state:
      refreshed.localDemoScenarioVersion === 1
        ? refreshed
        : { ...seedLocalDemoScenarios(refreshed), localDemoScenarioVersion: 1 },
  };
}
